import type { SourceKind } from './enums';
import { normalizePhone } from './phone';

export interface ResumeHints {
  lastName: string | null;
  firstName: string | null;
  middleName: string | null;
  /** E.164 */
  phone: string | null;
  email: string | null;
  /** Сайт вакансій, з якого вивантажено резюме (за згадкою його адреси). */
  sourceId: string | null;
}

/** Старі назви сайтів, які ще трапляються в резюме. */
const SOURCE_ALIASES: Record<string, string[]> = {
  'robota.ua': ['rabota.ua'],
};

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Сайт вакансій з довідника, чия адреса згадується в тексті чи назві файлу
 * (Work.ua, Robota.ua додають її у вивантажене резюме). Лише `job_board`: адреса пошти чи
 * «рекомендація» в тексті резюме нічого не кажуть про те, як воно надійшло.
 * Якщо згадано кілька сайтів — невідомо, який з них, тож нічого не підставляємо.
 */
export function detectResumeSource(
  text: string,
  sources: readonly { id: string; name: string; kind: SourceKind }[],
): string | null {
  const found = sources.filter((s) => {
    if (s.kind !== 'job_board') return false;
    const name = s.name.trim().toLowerCase();
    return [name, ...(SOURCE_ALIASES[name] ?? [])].some((n) =>
      new RegExp(`(^|[^\\p{L}\\d])${escapeRe(n)}($|[^\\p{L}\\d])`, 'iu').test(text),
    );
  });
  return found.length === 1 ? found[0]!.id : null;
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Послідовності цифр із розділювачами, схожі на телефон (10–13 цифр).
const PHONE_RE = /(?:\+?\d[\d\s()\-.]{8,18}\d)/g;
const CYR_PART = "[А-ЯҐЄІЇ][а-яґєіїʼ'’]+";
/** Слово з великої літери; подвійні прізвища через дефіс («Кам'янська-Шевчук»). */
const CYR_WORD = `${CYR_PART}(?:-${CYR_PART})?`;
const NAME_LINE_RE = new RegExp(`^(${CYR_WORD})\\s+(${CYR_WORD})(?:\\s+(${CYR_WORD}))?$`);
/** Слова, з яких зазвичай починаються заголовки резюме, а не ПІБ. */
const NOT_NAME = new Set([
  'Резюме',
  'Освіта',
  'Досвід',
  'Контакти',
  'Навички',
  'Мета',
  'Посада',
  'Бажана',
  'Персональні',
  'Особисті',
  'Інформація',
  'Мови',
  'Курси',
]);

function titleCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

const PATRONYMIC_RE = /(ович|евич|ївна|івна|овна|ична|інічна)$/i;

/** Підписи полів перед ПІБ: «ПІБ:», «Прізвище, ім'я, по батькові:», «Ім'я:». */
const NAME_LABEL_RE =
  /^(?:п\.?\s*і\.?\s*б\.?|прізвище,?\s*ім['ʼ’]я(?:,?\s*(?:та\s+)?по\s+батькові)?|ім['ʼ’]я)\s*[:\-–—]\s*/i;

/** Слова рядка: «ГУЛЯЄВ Юрій» → «Гуляєв Юрій». */
function normalizeLine(line: string): string {
  return line
    .replace(NAME_LABEL_RE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (w.length > 1 && w === w.toUpperCase() ? titleCase(w) : w))
    .join(' ');
}

/** Чи схожий рядок на ПІБ: 2–3 слова кирилицею з великої літери, жодне не є заголовком. */
function parseNameLine(
  line: string,
): Pick<ResumeHints, 'lastName' | 'firstName' | 'middleName'> | null {
  const m = NAME_LINE_RE.exec(normalizeLine(line));
  if (!m) return null;
  const words = [m[1], m[2], m[3]].filter((w): w is string => Boolean(w));
  if (words.some((w) => NOT_NAME.has(w))) return null;
  if (!m[3]) {
    // «Ірина Василівна» — ім'я й по батькові без прізвища: не ПІБ.
    if (PATRONYMIC_RE.test(m[2]!)) return null;
    return { lastName: m[1]!, firstName: m[2]!, middleName: null };
  }
  // «Прізвище Ім'я По батькові» — звичний порядок.
  if (PATRONYMIC_RE.test(m[3])) return { lastName: m[1]!, firstName: m[2]!, middleName: m[3] };
  // «Ім'я По батькові Прізвище» — по батькові посередині.
  if (PATRONYMIC_RE.test(m[2]!)) return { lastName: m[3], firstName: m[1]!, middleName: m[2]! };
  return null;
}

/** Рядок з одного слова-по батькові: продовження ПІБ, перенесене на новий рядок. */
function patronymicLine(line: string | undefined): string | null {
  const w = line ? normalizeLine(line) : '';
  return new RegExp(`^${CYR_WORD}$`).test(w) && PATRONYMIC_RE.test(w) ? w : null;
}

/** ПІБ з імені файлу: «Дорошенко_Юрій_резюме.pdf», «CV Дорошенко Юрій.docx». */
function nameFromFileName(fileName: string) {
  const base = fileName
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[_\-.]+/g, ' ')
    // `\b` у JS не розпізнає межі кириличних слів, тому межі задаємо пробілами.
    .replace(/(^|\s)(cv|resume|резюме)(?=\s|$)/giu, ' ')
    .trim();
  return parseNameLine(base);
}

/**
 * Дані з тексту резюме: ПІБ, телефон, email, сайт-джерело (регулярні вирази й евристики).
 * Форма підставляє їх лише в порожні поля, користувач перевіряє перед збереженням.
 */
export function extractResumeHints(
  text: string | null,
  fileName = '',
  sources: Parameters<typeof detectResumeSource>[1] = [],
): ResumeHints {
  const body = text ?? '';
  const hints: ResumeHints = {
    lastName: null,
    firstName: null,
    middleName: null,
    phone: null,
    email: null,
    sourceId: detectResumeSource(`${body}\n${fileName}`, sources),
  };

  hints.email = body.match(EMAIL_RE)?.[0]?.toLowerCase() ?? null;

  for (const candidate of body.match(PHONE_RE) ?? []) {
    const p = normalizePhone(candidate);
    if (p.e164) {
      hints.phone = p.e164;
      break;
    }
  }

  const lines = body
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 15);
  let fromText: ReturnType<typeof parseNameLine> = null;
  for (let i = 0; i < lines.length && !fromText; i++) {
    fromText = parseNameLine(lines[i]!);
    if (fromText && !fromText.middleName) {
      fromText.middleName = patronymicLine(lines[i + 1]);
    }
  }
  const name = fromText ?? nameFromFileName(fileName);
  if (name) Object.assign(hints, name);
  return hints;
}
